// File: frontend/src/components/CaseManagement.js
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Folder, Users, Clock, ChevronDown, ChevronRight, Plus, Search,
  Edit2, Trash2, User,
  UserPlus, FileText
} from 'lucide-react';
import { casesAPI, peopleAPI } from '../utils/api';
import ReportGenerator from './ReportGenerator';
import { useData } from '../contexts/DataContext';
import { useUI } from '../contexts/UIContext';

const CaseManagement = () => {
  const { t } = useTranslation();
  const { people, fetchPeople } = useData();
  const { setEditingPerson, setSelectedPersonForDetail } = useUI();
  const [cases, setCases] = useState([]);
  const [expandedCases, setExpandedCases] = useState({});
  const [editingCase, setEditingCase] = useState(null);
  const [showNewCaseForm, setShowNewCaseForm] = useState(false);
  const [newCaseData, setNewCaseData] = useState({ case_name: '', description: '', status: 'active' });
  const [searchTerm, setSearchTerm] = useState('');
  const [showQuickAdd, setShowQuickAdd] = useState(null);
  const [quickAddPerson, setQuickAddPerson] = useState({ firstName: '', lastName: '' });
  const [showAddPeopleModal, setShowAddPeopleModal] = useState(null);
  const [selectedPeopleToAdd, setSelectedPeopleToAdd] = useState([]);
  const [showReportGenerator, setShowReportGenerator] = useState(false);
  const [reportCaseId, setReportCaseId] = useState(null);

  useEffect(() => {
    fetchCases();
  }, [people]);

  const fetchCases = async () => {
    try {
      const casesData = await casesAPI.getAll();
      
      // Process cases to add people count and other stats
      const processedCases = casesData.map(caseItem => {
        const casePeople = people.filter(p => p.case_name === caseItem.case_name);
        const lastUpdate = Math.max(
          ...casePeople.map(p => new Date(p.updated_at || p.created_at).getTime()),
          new Date(caseItem.updated_at || caseItem.created_at).getTime()
        );
        
        return {
          ...caseItem,
          peopleCount: casePeople.length,
          people: casePeople,
          lastUpdate: new Date(lastUpdate),
          openCount: casePeople.filter(p => p.status === 'Open').length,
          investigatingCount: casePeople.filter(p => p.status === 'Being Investigated').length,
          closedCount: casePeople.filter(p => p.status === 'Closed').length
        };
      });
      
      setCases(processedCases.sort((a, b) => b.lastUpdate - a.lastUpdate));
    } catch (error) {
      console.error('Error fetching cases:', error);
    }
  };

  const handleCreateCase = async () => {
    if (!newCaseData.case_name.trim()) return;
    
    try {
      await casesAPI.create(newCaseData);
      setNewCaseData({ case_name: '', description: '', status: 'active' });
      setShowNewCaseForm(false);
      fetchCases();
    } catch (error) {
      console.error('Error creating case:', error);
      alert(t('caseManagement.errorCreateCase'));
    }
  };

  const handleUpdateCase = async (caseId, updates) => {
    try {
      await casesAPI.update(caseId, updates);
      fetchCases();
      setEditingCase(null);
    } catch (error) {
      console.error('Error updating case:', error);
      alert(t('caseManagement.errorUpdateCase'));
    }
  };

  const handleDeleteCase = async (caseId) => {
    if (window.confirm(t('caseManagement.confirmDeleteCase'))) {
      try {
        await casesAPI.delete(caseId);
        fetchCases();
      } catch (error) {
        console.error('Error deleting case:', error);
        alert(t('caseManagement.errorDeleteCase'));
      }
    }
  };

  const handleQuickAddPerson = async (caseName) => {
    if (!quickAddPerson.firstName.trim()) return;
    
    try {
      const newPerson = {
        firstName: quickAddPerson.firstName,
        lastName: quickAddPerson.lastName,
        caseName: caseName,
        status: 'Open',
        category: 'Person of Interest',
        aliases: [],
        osintData: [],
        connections: [],
        locations: [],
        attachments: [],
        custom_fields: {}
      };
      
      await peopleAPI.create(newPerson);
      setQuickAddPerson({ firstName: '', lastName: '' });
      setShowQuickAdd(null);
      fetchPeople();
    } catch (error) {
      console.error('Error creating person:', error);
      alert(t('caseManagement.errorCreatePerson'));
    }
  };

  const handleAddPeopleToCase = async (caseName) => {
    for (const personId of selectedPeopleToAdd) {
      const person = people.find(p => p.id === personId);
      if (person) {
        const updatedPerson = {
          firstName: person.first_name,
          lastName: person.last_name,
          aliases: person.aliases,
          dateOfBirth: person.date_of_birth,
          category: person.category,
          status: person.status,
          crmStatus: person.crm_status,
          caseName: caseName,
          profilePictureUrl: person.profile_picture_url,
          notes: person.notes,
          osintData: person.osint_data,
          attachments: person.attachments,
          connections: person.connections,
          locations: person.locations,
          custom_fields: person.custom_fields
        };
        
        await peopleAPI.update(personId, updatedPerson);
      }
    }
    
    setSelectedPeopleToAdd([]);
    setShowAddPeopleModal(null);
    fetchPeople();
  };

  const toggleCase = (caseId) => {
    setExpandedCases(prev => ({
      ...prev,
      [caseId]: !prev[caseId]
    }));
  };

  const getFullName = (person) => {
    return `${person.first_name || ''} ${person.last_name || ''}`.trim();
  };

  const getAge = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const getTimeAgo = (date) => {
    const now = new Date();
    const diff = now - date;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) return t('caseManagement.timeAgo.today');
    if (days === 1) return t('caseManagement.timeAgo.yesterday');
    if (days < 7) return t('caseManagement.timeAgo.daysAgo', { count: days });
    if (days < 30) return t('caseManagement.timeAgo.weeksAgo', { count: Math.floor(days / 7) });
    if (days < 365) return t('caseManagement.timeAgo.monthsAgo', { count: Math.floor(days / 30) });
    return t('caseManagement.timeAgo.yearsAgo', { count: Math.floor(days / 365) });
  };

  const filteredCases = cases.filter(caseItem => 
    caseItem.case_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    caseItem.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const availablePeople = people.filter(p => 
    !p.case_name || p.case_name === ''
  );

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100">{t('caseManagement.title')}</h1>
        <button
          onClick={() => setShowNewCaseForm(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('caseManagement.newCase')}
        </button>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-slate-500 w-5 h-5" />
          <input
            type="text"
            placeholder={t('caseManagement.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* New Case Form */}
      {showNewCaseForm && (
        <div className="mb-6 p-4 bg-white dark:bg-slate-800 rounded-lg shadow-sm border">
          <h3 className="text-lg font-semibold mb-4">{t('caseManagement.createNewCase')}</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">{t('caseManagement.caseName')}</label>
              <input
                type="text"
                value={newCaseData.case_name}
                onChange={(e) => setNewCaseData({ ...newCaseData, case_name: e.target.value })}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t('caseManagement.caseNamePlaceholder')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">{t('caseManagement.description')}</label>
              <textarea
                value={newCaseData.description}
                onChange={(e) => setNewCaseData({ ...newCaseData, description: e.target.value })}
                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows="3"
                placeholder={t('caseManagement.descriptionPlaceholder')}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <button
                onClick={() => {
                  setShowNewCaseForm(false);
                  setNewCaseData({ case_name: '', description: '', status: 'active' });
                }}
                className="px-4 py-2 text-gray-700 dark:text-slate-300 bg-gray-100 dark:bg-slate-700 rounded-md hover:bg-gray-200"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleCreateCase}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                {t('caseManagement.createCase')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cases List */}
      <div className="space-y-4">
        {filteredCases.map(caseItem => (
          <div key={caseItem.id} className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border">
            {/* Case Header */}
            <div className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <button
                    onClick={() => toggleCase(caseItem.id)}
                    className="text-gray-500 dark:text-slate-400 hover:text-gray-700"
                  >
                    {expandedCases[caseItem.id] ? (
                      <ChevronDown className="w-5 h-5" />
                    ) : (
                      <ChevronRight className="w-5 h-5" />
                    )}
                  </button>
                  <Folder className="w-5 h-5 text-blue-600" />
                  <h3 className="text-lg font-semibold">{caseItem.case_name}</h3>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    caseItem.status === 'active' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400' :
                    caseItem.status === 'closed' ? 'bg-gray-100 dark:bg-slate-700 text-gray-800 dark:text-slate-200' :
                    'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400'
                  }`}>
                    {caseItem.status}
                  </span>
                </div>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => {
                      setReportCaseId(caseItem.id);
                      setShowReportGenerator(true);
                    }}
                    className="text-blue-600 hover:text-blue-700"
                    title={t('dashboard.generateReport')}
                  >
                    <FileText className="w-4 h-4" />
                  </button>
                  <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                    <Users className="w-4 h-4" />
                    <span>{t('caseManagement.peopleCount', { count: caseItem.peopleCount })}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                    <Clock className="w-4 h-4" />
                    <span>{getTimeAgo(caseItem.lastUpdate)}</span>
                  </div>
                </div>
              </div>
              
              {!expandedCases[caseItem.id] && (
                <div className="mt-3 flex items-center space-x-6 text-sm">
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="text-gray-600 dark:text-gray-400">{t('caseManagement.open', { count: caseItem.openCount })}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                    <span className="text-gray-600 dark:text-gray-400">{t('caseManagement.investigating', { count: caseItem.investigatingCount })}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <div className="w-3 h-3 bg-gray-500 rounded-full"></div>
                    <span className="text-gray-600 dark:text-gray-400">{t('caseManagement.closed', { count: caseItem.closedCount })}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Expanded Case Content */}
            {expandedCases[caseItem.id] && (
              <div className="border-t">
                {/* Case Settings */}
                <div className="p-4 bg-gray-50 dark:bg-slate-900">
                  {editingCase === caseItem.id ? (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">{t('caseManagement.caseName')}</label>
                        <input
                          type="text"
                          value={caseItem.case_name}
                          onChange={(e) => {
                            const updatedCases = cases.map(c =>
                              c.id === caseItem.id ? { ...c, case_name: e.target.value } : c
                            );
                            setCases(updatedCases);
                          }}
                          className="w-full px-3 py-2 border rounded-md"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">{t('caseManagement.description')}</label>
                        <textarea
                          value={caseItem.description || ''}
                          onChange={(e) => {
                            const updatedCases = cases.map(c =>
                              c.id === caseItem.id ? { ...c, description: e.target.value } : c
                            );
                            setCases(updatedCases);
                          }}
                          className="w-full px-3 py-2 border rounded-md"
                          rows="2"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">{t('caseManagement.status')}</label>
                        <select
                          value={caseItem.status}
                          onChange={(e) => {
                            const updatedCases = cases.map(c =>
                              c.id === caseItem.id ? { ...c, status: e.target.value } : c
                            );
                            setCases(updatedCases);
                          }}
                          className="px-3 py-2 border rounded-md"
                        >
                          <option value="active">{t('caseManagement.statusActive')}</option>
                          <option value="on_hold">{t('caseManagement.statusOnHold')}</option>
                          <option value="closed">{t('caseManagement.statusClosed')}</option>
                        </select>
                      </div>
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => setEditingCase(null)}
                          className="px-3 py-1 text-gray-700 dark:text-slate-300 bg-gray-200 dark:bg-slate-600 rounded-md hover:bg-gray-300"
                        >
                          {t('common.cancel')}
                        </button>
                        <button
                          onClick={() => handleUpdateCase(caseItem.id, {
                            case_name: caseItem.case_name,
                            description: caseItem.description,
                            status: caseItem.status
                          })}
                          className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                        >
                          {t('common.save')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{caseItem.description || t('caseManagement.noDescription')}</p>
                        <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                          {t('caseManagement.createdOn', { date: new Date(caseItem.created_at).toLocaleDateString() })}
                        </p>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => setEditingCase(caseItem.id)}
                          className="text-gray-600 dark:text-slate-400 hover:text-gray-700"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteCase(caseItem.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* People in Case */}
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium">{t('caseManagement.peopleInCase')}</h4>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setShowAddPeopleModal(caseItem.case_name)}
                        className="px-3 py-1 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 rounded-md hover:bg-gray-200 flex items-center text-sm"
                      >
                        <UserPlus className="w-4 h-4 mr-1" />
                        {t('caseManagement.addExisting')}
                      </button>
                      <button
                        onClick={() => setShowQuickAdd(caseItem.case_name)}
                        className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center text-sm"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        {t('caseManagement.quickAdd')}
                      </button>
                    </div>
                  </div>

                  {/* Quick Add Form */}
                  {showQuickAdd === caseItem.case_name && (
                    <div className="mb-4 p-3 bg-gray-50 dark:bg-slate-900 rounded-lg">
                      <div className="flex space-x-2">
                        <input
                          type="text"
                          placeholder={t('caseManagement.firstNamePlaceholder')}
                          value={quickAddPerson.firstName}
                          onChange={(e) => setQuickAddPerson({ ...quickAddPerson, firstName: e.target.value })}
                          className="flex-1 px-3 py-2 border rounded-md text-sm"
                        />
                        <input
                          type="text"
                          placeholder={t('caseManagement.lastNamePlaceholder')}
                          value={quickAddPerson.lastName}
                          onChange={(e) => setQuickAddPerson({ ...quickAddPerson, lastName: e.target.value })}
                          className="flex-1 px-3 py-2 border rounded-md text-sm"
                        />
                        <button
                          onClick={() => handleQuickAddPerson(caseItem.case_name)}
                          className="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm"
                        >
                          {t('common.add')}
                        </button>
                        <button
                          onClick={() => {
                            setShowQuickAdd(null);
                            setQuickAddPerson({ firstName: '', lastName: '' });
                          }}
                          className="px-3 py-2 text-gray-700 dark:text-slate-300 bg-gray-200 dark:bg-slate-600 rounded-md hover:bg-gray-300 text-sm"
                        >
                          {t('common.cancel')}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* People List */}
                  <div className="space-y-2">
                    {caseItem.people.map(person => (
                      <div key={person.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-900 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800">
                        <div className="flex items-center space-x-3">
                          <User className="w-4 h-4 text-gray-400 dark:text-slate-500" />
                          <div>
                            <p className="font-medium">
                              {getFullName(person)}
                              {person.date_of_birth && (
                                <span className="text-gray-500 dark:text-slate-400 ml-2">
                                  ({getAge(person.date_of_birth)})
                                </span>
                              )}
                            </p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                              {person.category} • {person.status}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {t('caseManagement.updatedTimeAgo', { time: getTimeAgo(new Date(person.updated_at || person.created_at)) })}
                          </span>
                          <button
                            onClick={() => setSelectedPersonForDetail(person)}
                            className="text-blue-600 hover:text-blue-700 text-sm"
                          >
                            {t('common.view')}
                          </button>
                          <button
                            onClick={() => setEditingPerson(person)}
                            className="text-gray-600 dark:text-slate-400 hover:text-gray-700"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add People Modal */}
      {showAddPeopleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-md">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold">{t('caseManagement.addPeopleToCase', { caseName: showAddPeopleModal })}</h3>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-600 dark:text-slate-400 mb-4">{t('caseManagement.selectPeopleHint')}</p>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {availablePeople.map(person => (
                  <label key={person.id} className="flex items-center space-x-3 p-2 hover:bg-gray-50 dark:hover:bg-slate-800 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedPeopleToAdd.includes(person.id)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedPeopleToAdd([...selectedPeopleToAdd, person.id]);
                        } else {
                          setSelectedPeopleToAdd(selectedPeopleToAdd.filter(id => id !== person.id));
                        }
                      }}
                      className="h-4 w-4 text-blue-600 rounded"
                    />
                    <div className="flex-1">
                      <p className="font-medium">{getFullName(person)}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{person.category}</p>
                    </div>
                  </label>
                ))}
              </div>
              <div className="flex justify-end space-x-2 mt-6">
                <button
                  onClick={() => {
                    setShowAddPeopleModal(null);
                    setSelectedPeopleToAdd([]);
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-slate-300 bg-gray-100 dark:bg-slate-700 rounded-md hover:bg-gray-200"
                >
                  {t('common.cancel')}
                </button>
                <button
                  onClick={() => handleAddPeopleToCase(showAddPeopleModal)}
                  disabled={selectedPeopleToAdd.length === 0}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {t('caseManagement.addNPeople', { count: selectedPeopleToAdd.length })}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Report Generator Modal */}
      {showReportGenerator && (
        <ReportGenerator 
          caseId={reportCaseId}
          onClose={() => {
            setShowReportGenerator(false);
            setReportCaseId(null);
          }}
        />
      )}
    </div>
  );
};

export default CaseManagement;